import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "@/components/ui/Card.jsx";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import Switch from "@/sot/primitives/Switch.jsx";
import SettingsToggleRow from "@/sot/primitives/SettingsToggleRow.jsx";
import supabase from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  applyAccessibilityFolaPrefs,
  DEFAULT_ACCESSIBILITY_FOLA_PREFS,
  mapAccessibilityFolaPrefsToDb,
  normalizeAccessibilityFolaPrefs,
  readAccessibilityFolaPrefs,
  writeAccessibilityFolaPrefs,
} from "@/settings/accessibility.js";

function isMissingAccessibilityPrefsColumn(error) {
  const msg = String(error?.message || "").toLowerCase();
  return msg.includes("accessibility_fola_prefs") && msg.includes("column");
}

function toBool(next) {
  if (typeof next === "boolean") return next;
  if (next && typeof next === "object" && "target" in next) {
    return Boolean(next.target?.checked);
  }
  return Boolean(next);
}

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

function prefUpdater(setPrefs, key, value) {
  setPrefs((current) => {
    const next = normalizeAccessibilityFolaPrefs({ ...current, [key]: value });
    writeAccessibilityFolaPrefs(next);
    applyAccessibilityFolaPrefs(next);
    return next;
  });
}

export default function AccessibilityFolaCommon({ role = "patient" }) {
  const navigate = useNavigate();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const basePath = `/settings/${role === "doctor" ? "doctor" : "patient"}`;

  const [prefs, setPrefs] = React.useState(() => readAccessibilityFolaPrefs());
  const [hydrated, setHydrated] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user || !alive) {
          if (alive) setHydrated(true);
          return;
        }

        const { data, error } = await supabase
          .from("user_preferences")
          .select("accessibility_fola_prefs")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          if (!isMissingAccessibilityPrefsColumn(error)) {
            console.error("Accessibility & Fola hydrate failed", error);
          }
          if (alive) setHydrated(true);
          return;
        }

        if (!alive) return;

        const fromDb = normalizeAccessibilityFolaPrefs(
          data?.accessibility_fola_prefs
        );
        setPrefs(fromDb);
        writeAccessibilityFolaPrefs(fromDb);
        applyAccessibilityFolaPrefs(fromDb);
      } finally {
        if (alive) setHydrated(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    applyAccessibilityFolaPrefs(prefs);
    writeAccessibilityFolaPrefs(prefs);
  }, [prefs]);

  React.useEffect(() => {
    if (!hydrated) return undefined;

    const timer = setTimeout(async () => {
      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user) return;

        const { error } = await supabase.from("user_preferences").upsert(
          {
            user_id: user.id,
            accessibility_fola_prefs: mapAccessibilityFolaPrefsToDb(prefs),
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

        if (error) {
          if (!isMissingAccessibilityPrefsColumn(error)) {
            console.error("Accessibility & Fola save failed", error);
          }
          return;
        }
      } catch (error) {
        console.error("Accessibility & Fola save failed", error);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [prefs, hydrated]);

  React.useEffect(() => {
    if (!snackbar) return undefined;
    const timer = window.setTimeout(() => setSnackbar(""), 2400);
    return () => window.clearTimeout(timer);
  }, [snackbar]);

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        onBack={() => navigate(`${basePath}/preferences`)}
        title={t("settings.preferences.accessibility.title", isFrench ? "Accessibilité et Fola" : "Accessibility & Fola")}
      />

      <div className="mt-6 space-y-8">
        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {t("settings.preferences.accessibility.folaAssistance", isFrench ? "Assistance Fola" : "Fola assistance")}
          </h2>
          <p className="px-0.5 text-body-small text-on-surface-var">
            {t(
              "settings.preferences.accessibility.folaAssistanceCopy",
              isFrench
                ? "Choisissez comment Fola vous accompagne dans AfriMed™. Fola peut aider à expliquer les soins et à préparer les rendez-vous, mais ne pose pas de diagnostic et ne remplace pas votre clinicien."
                : "Choose how Fola supports you across AfriMed™. Fola can help explain care and prepare you for appointments, but it does not diagnose or replace your clinician.",
            )}
          </p>

          <Card className="overflow-hidden">
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.summaries.title",
                isFrench ? "Résumer mes notes de consultation" : "Summarise my visit notes",
              )}
              support={t(
                "settings.preferences.accessibility.rows.summaries.support",
                isFrench ? "Explications simples après les rendez-vous" : "Simple follow-up explanations after appointments",
              )}
              trailing={
                <Switch
                  checked={prefs.folaSummaries}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "folaSummaries", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider
            />
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.medicines.title",
                isFrench ? "Expliquer mes médicaments" : "Explain my medicines",
              )}
              support={t(
                "settings.preferences.accessibility.rows.medicines.support",
                isFrench ? "M’aider à comprendre les ordonnances dans un langage simple" : "Help me understand prescriptions in everyday language",
              )}
              trailing={
                <Switch
                  checked={prefs.folaExplainMeds}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "folaExplainMeds", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider
            />
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.questions.title",
                isFrench ? "M’aider à préparer mes questions" : "Help me prepare questions",
              )}
              support={t(
                "settings.preferences.accessibility.rows.questions.support",
                isFrench ? "Suggérer des questions utiles avant une consultation" : "Suggest useful questions before a consultation",
              )}
              trailing={
                <Switch
                  checked={prefs.folaPrepQuestions}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "folaPrepQuestions", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider={false}
            />
          </Card>
        </section>

        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {t("settings.preferences.accessibility.heading", isFrench ? "Accessibilité" : "Accessibility")}
          </h2>
          <p className="px-0.5 text-body-small text-on-surface-var">
            {t(
              "settings.preferences.accessibility.copy",
              isFrench
                ? "Personnalisez AfriMed™ pour une lecture plus confortable, une utilisation plus calme et une navigation plus claire."
                : "Personalise AfriMed™ so it is easier to read, calmer to use, and clearer to navigate.",
            )}
          </p>

          <Card className="overflow-hidden">
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.largeText.title",
                isFrench ? "Texte et icônes plus grands" : "Larger text & icons",
              )}
              support={t(
                "settings.preferences.accessibility.rows.largeText.support",
                isFrench ? "Augmente la taille des textes importants et le confort des zones tactiles" : "Increase key text sizes and touch-target comfort",
              )}
              trailing={
                <Switch
                  checked={prefs.largeText}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "largeText", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider
            />
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.reduceMotion.title",
                isFrench ? "Réduire les animations" : "Reduce motion",
              )}
              support={t(
                "settings.preferences.accessibility.rows.reduceMotion.support",
                isFrench ? "Limite les animations et les mouvements de l’interface" : "Limit animations and interface movement",
              )}
              trailing={
                <Switch
                  checked={prefs.reduceMotion}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "reduceMotion", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider
            />
            <SettingsToggleRow
              title={t(
                "settings.preferences.accessibility.rows.highContrast.title",
                isFrench ? "Mode contraste élevé" : "High-contrast mode",
              )}
              support={t(
                "settings.preferences.accessibility.rows.highContrast.support",
                isFrench ? "Améliore la lisibilité grâce à un contraste renforcé" : "Increase readability with stronger contrast",
              )}
              trailing={
                <Switch
                  checked={prefs.highContrast}
                  onChange={(next) => {
                    prefUpdater(setPrefs, "highContrast", toBool(next));
                    setSnackbar(
                      t(
                        "settings.preferences.accessibility.updated",
                        isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
                      ),
                    );
                  }}
                />
              }
              divider={false}
            />
          </Card>
        </section>
      </div>

      <Snackbar open={!!snackbar} onClose={() => setSnackbar("")}>
        {snackbar ||
          t(
            "settings.preferences.accessibility.updated",
            isFrench ? "Accessibilité et Fola mis à jour" : "Accessibility & Fola updated",
          )}
      </Snackbar>
    </SettingsScreenShell>
  );
}

export { DEFAULT_ACCESSIBILITY_FOLA_PREFS };
